import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X, Gift, Copy, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralPopup = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPopup, setShowPopup] = useState(false);
  const [referrerName, setReferrerName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [discount, setDiscount] = useState(10);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const ref = searchParams.get('ref');
    
    if (ref) {
      // Validate referral code and get referrer info
      const validateReferral = async () => {
        try {
          const response = await axios.post(`${API}/promo-codes/validate`, {
            code: ref.toUpperCase()
          });
          
          if (response.data.success && response.data.isReferral) {
            setReferrerName(response.data.referrerName || 'A friend');
            setReferralCode(ref.toUpperCase());
            setDiscount(response.data.discount * 100);
            setShowPopup(true);
            
            // Store referral code in session storage for checkout
            sessionStorage.setItem('referral_code', ref.toUpperCase());
          }
        } catch (e) {
          console.error('Error validating referral:', e);
        }
      };
      
      validateReferral();
      
      // Remove the ref param from URL without reload
      searchParams.delete('ref');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setShowPopup(false);
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white dark:bg-neutral-900 rounded-2xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-300"
        data-testid="referral-popup"
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-2xl p-6 text-white relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
            data-testid="close-referral-popup"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <Gift size={28} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">You've Been Referred!</h2>
              <p className="text-emerald-100 text-sm">By {referrerName}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="text-center">
            <p className="text-gray-600 dark:text-neutral-400 mb-2">
              {referrerName} has invited you to try Connectify eSIM
            </p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              Get {discount}% OFF your first order!
            </p>
          </div>

          {/* Referral Code Display */}
          <div className="bg-gray-50 dark:bg-neutral-950 rounded-xl p-4">
            <p className="text-xs text-gray-500 dark:text-neutral-500 mb-2 text-center">Your referral code</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-2xl font-mono font-bold text-gray-900 dark:text-neutral-100 tracking-wider">
                {referralCode}
              </span>
              <button
                onClick={handleCopyCode}
                className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 transition-colors"
                data-testid="copy-referral-code"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-emerald-50 dark:bg-emerald-500/10 rounded-xl p-4 border border-emerald-100">
            <p className="text-sm text-emerald-800">
              <strong>How to use:</strong> Enter this code at checkout to receive your {discount}% discount!
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleClose}
            className="w-full py-3 bg-black text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
            data-testid="start-shopping-btn"
          >
            Start Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferralPopup;
